import _ from 'underscore';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
	DndContext,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors
} from '@dnd-kit/core';

import CardDraggable from './CardDraggable';
import CardDropable from './CardDropable';
import MatchingCardContent from './MatchingCardContent';

import styles from './styles.module.scss';

const replaceChildrenId = (children, id) => {
	const stringToRepalce = id.includes('central') ? 'central' : 'free';
	const replaceString = id.includes('central') ? 'free' : 'central';
	const newId = id.replace(stringToRepalce, replaceString);
	return {
		...children,
		id: newId,
		layout: {
			...children.layout,
			props: {
				...children.layout.props,
				id: newId
			}
		}
	};
};

const Matchings = ({ variants, selectVariant, questionNumber }) => {
	const exampleCards = variants.map((variant) => ({
		...variant.left,
		position: variant.position,
		key: `example-${variant.position}`
	}));

	const [centralCards, setCentralCards] = useState(
		variants.map((variant, index) => ({
			id: `droppable-${index}`,
			position: variant.position,
			children: null
		}))
	);

	const [freeCards, setFreeCards] = useState(
		_.shuffle(
			variants.map((variant, index) => ({
				id: `droppable-${variants.length + index}`,
				position: variant.position,
				children: {
					id: `card-free-${index}`,
					data: { ...variant.right },
					correctLeftIndex: index,
					layout: (
						<CardDraggable
							id={`card-free-${index}`}
							position={variant.position}
							{...variant.right}
						/>
					)
				}
			}))
		)
	);

	const mouseSensor = useSensor(MouseSensor, {
		activationConstraint: {
			distance: 10
		}
	});

	const touchSensor = useSensor(TouchSensor, {
		activationConstraint: {
			delay: 120
		}
	});

	const sensors = useSensors(mouseSensor, touchSensor);

	const addItemsToCard = (event) => {
		if (event?.over?.id && event?.active?.id) {
			const draggedId = event.active.id;
			const droppedOnId = event.over.id;
			const isDroppedOnCenter = event.over.data.current.type === 'central';
			const isDroppedOnFree = !isDroppedOnCenter;
			const isDroppedFromCenter = draggedId.includes('central');
			const isDroppedFromFree = !isDroppedFromCenter;

			if (isDroppedFromFree && isDroppedOnCenter) {
				const newCentralCards = centralCards.map((card) => {
					if (card.id === droppedOnId) {
						const newChildren = freeCards.find(
							(freeCard) => freeCard.children?.id === draggedId
						).children;
						return {
							...card,
							children: newChildren
								? replaceChildrenId(newChildren, draggedId)
								: newChildren
						};
					}
					return card;
				});

				const newFreeCards = freeCards.map((card) => {
					if (card?.children?.id === draggedId) {
						const newChildren = centralCards.find(
							(centralCard) => centralCard.id === droppedOnId
						).children;
						return {
							...card,
							children: newChildren
								? replaceChildrenId(newChildren, newChildren.id)
								: newChildren
						};
					}

					return card;
				});
				setCentralCards(newCentralCards);
				setFreeCards(newFreeCards);
			}

			if (isDroppedFromCenter && isDroppedOnCenter) {
				const newCentralCards = centralCards.map((card) => {
					if (card.id === droppedOnId) {
						const newChildren = centralCards.find(
							(centralCard) => centralCard?.children?.id === draggedId
						).children;
						return {
							...card,
							children: newChildren || null
						};
					}
					if (card?.children?.id === draggedId) {
						const newChildren = centralCards.find(
							(centralCard) => centralCard.id === droppedOnId
						)?.children;
						return { ...card, children: newChildren || null };
					}
					return card;
				});
				setCentralCards(newCentralCards);
			}

			if (isDroppedFromCenter && isDroppedOnFree) {
				const newCentralCards = centralCards.map((card) => {
					if (card?.children?.id === draggedId) {
						const newChildren = freeCards.find(
							(freeCard) => freeCard.id === droppedOnId
						)?.children;
						return {
							...card,
							children: newChildren
								? replaceChildrenId(newChildren, newChildren.id)
								: newChildren
						};
					}
					return card;
				});

				const newFreeCards = freeCards.map((card) => {
					if (card?.id === droppedOnId) {
						const newChildren = centralCards.find(
							(centralCard) => centralCard?.children?.id === draggedId
						)?.children;
						return {
							...card,
							children: newChildren
								? replaceChildrenId(newChildren, draggedId)
								: newChildren
						};
					}
					return card;
				});
				setCentralCards(newCentralCards);
				setFreeCards(newFreeCards);
			}
		}
	};

	useEffect(() => {
		const areAllCardsFilled =
			centralCards.filter((card) => !!card.children).length ===
			centralCards.length;
		if (areAllCardsFilled) {
			const isCorrect = !variants.find(
				(item, index) => centralCards[index].children.correctLeftIndex !== index
			);
			selectVariant({ isCorrect, isFilled: true }, questionNumber);
		} else {
			selectVariant({ isCorrect: false, isFilled: false }, questionNumber);
		}
	}, [centralCards]);

	return (
		<DndContext onDragEnd={addItemsToCard} sensors={sensors}>
			<div className={styles.Matchings}>
				<div className={styles.Matchings__Group}>
					<div className={styles.Matchings__List}>
						{exampleCards.map(({ audio, image, text, video, key }) => (
							<div key={key} className={styles.Matchings__WrapCardStatic}>
								<MatchingCardContent
									{...{
										audio,
										image,
										text,
										video
									}}
									typeCard="example"
								/>
							</div>
						))}
					</div>
					<div className={styles.Matchings__List}>
						{centralCards.map((card) => (
							<CardDropable
								key={card.id}
								id={card.id}
								borderType="dashed"
								data={{ type: 'central', activeId: card.children?.id }}
							>
								{card.children?.layout}
							</CardDropable>
						))}
					</div>
				</div>
				<div className={styles.Matchings__List}>
					{freeCards.map((card) => (
						<CardDropable
							key={card.id}
							id={card.id}
							borderType="solid"
							data={{ type: 'free', activeId: card.children?.id }}
						>
							{card.children?.layout}
						</CardDropable>
					))}
				</div>
			</div>
		</DndContext>
	);
};

Matchings.propTypes = {
	variants: PropTypes.array,
	selectVariant: PropTypes.func,
	questionNumber: PropTypes.number,
	selectedVariants: PropTypes.array
};

export default Matchings;
